import React, { useState, useRef, useMemo, useEffect, memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { FeatureType, BusinessIdea, LocationSuggestion, RecentScan, LandingLoginSource } from '@/types';
import AnalysisResultView from '@/components/AnalysisResult';
import BusinessDetail from '@/components/BusinessDetail';
import DiscoveryDossier from '@/components/DiscoveryDossier';
import CompareView from '@/components/CompareView';
import MapPicker from '@/components/MapPicker';
import UpgradeModal from '@/components/UpgradeModal';
import LegalAcknowledgmentModal from '@/components/LegalAcknowledgmentModal';
import RecoverKeyModal from '@/components/RecoverKeyModal';
import SignOutModal from '@/components/SignOutModal';
import SupportModal from '@/components/SupportModal';
import EditProfileModal from '@/components/EditProfileModal';
import SettingsModal from '@/components/SettingsModal';
import Sidebar from '@/components/Sidebar';
import Breadcrumbs from '@/components/Breadcrumbs';
import Header from '@/components/Header';
import Launchpad from '@/components/Launchpad';
import DiscoveryHub from '@/components/DiscoveryHub';
import OptimizeHub from '@/components/OptimizeHub';
import OptimizeDashboard from '@/components/OptimizeDashboard';
import RecentScans from '@/components/RecentScans';
import ScrutinyHub from '@/components/ScrutinyHub';
import HowToUse from '@/components/HowToUse';
import PricingOverview from '@/components/PricingOverview';
import ScrutinyDeck from '@/components/ScrutinyDeck';
import SavedIntelDrawer from '@/components/SavedIntelDrawer';
import OptimizeUpgradeFlow from '@/components/OptimizeUpgradeFlow';
import InteractiveHeroPreview from '@/components/InteractiveHeroPreview';
import { useVentureIntelligence } from '@/hooks/useVentureIntelligence';
import { useAuth } from '@/src/contexts/AuthContext';
import { useSearch } from '@/src/contexts/SearchContext';
import { useReporting } from '@/hooks/useReporting';
import { COUNTRIES, CURRENCIES } from '@/constants';
import ErrorBoundary from '@/components/ErrorBoundary';
import { TermsOfServiceScreen, PrivacyPolicyScreen } from '@/components/LegalScreens';
import { ArbitrationAgreementScreen } from '@/components/ArbitrationAgreementScreen';
import { EnhancedAppFeatures } from '@/components/EnhancedAppFeatures';
import { CookiePolicyModal } from '@/components/CookiePolicyModal';
import { CookiePolicyScreen } from '@/components/CookiePolicyScreen';
import { DataProcessingAgreementScreen } from '@/components/DataProcessingAgreementScreen';
import { CookieBanner } from '@/components/CookieBanner';
import { CookieCustomizeModal } from '@/components/CookieCustomizeModal';
import { PrivacySettings } from '@/components/PrivacySettings';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { defaultConsent, euDefaultConsent } from '@/types';
import { AnimatePresence } from 'motion/react';
import { db, doc, collection, setDoc, getDoc, query, orderBy, limit, addDoc, deleteDoc, updateDoc, handleFirestoreError, OperationType } from '@/firebase';
import { runAnalysis, getLocationSuggestions, geocodeLocation } from '@/services/apiServices';
import { getExchangeRates } from '@/services/currencyService';

const ModalPortal = memo(({ children }: { children: React.ReactNode }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? createPortal(children, document.body) : null;
});

const App: React.FC = () => {
  const { 
    state, 
    dispatch, 
    handleRunScan, 
    handleLocInputChange, 
    handleLaunchpadSectorChange, 
    handleDensityInputChange, 
    handleSuggestionSelect, 
    handleMapSelection, 
    handleRecentClick 
  } = useSearch();

  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({ USD: 1 });

  const maxBudgetUSD = 1000000;
  const minBudgetUSD = 5000;

  const currentMaxBudget = useMemo(() => {
    const rate = exchangeRates[state.preferredCurrency] || 1;
    return Math.round(maxBudgetUSD * rate);
  }, [exchangeRates, state.preferredCurrency]);

  const currentMinBudget = useMemo(() => {
    const rate = exchangeRates[state.preferredCurrency] || 1;
    return Math.round(minBudgetUSD * rate);
  }, [exchangeRates, state.preferredCurrency]);

  const [selectedIdea, setSelectedIdea] = useState<BusinessIdea | null>(null);
  const [isSavedMenuOpen, setIsSavedMenuOpen] = useState(false);
  const [isPurgeConfirmOpen, setIsPurgeConfirmOpen] = useState(false);
  const [isCompareViewOpen, setIsCompareViewOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [settingsInitialView, setSettingsInitialView] = useState<string | null>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isTechnicalSuiteOpen, setIsTechnicalSuiteOpen] = useState(false);
  const [isScrutinyActive, setIsScrutinyActive] = useState(false);
  const [savedHubFilter, setSavedHubFilter] = useState<'all' | 'launchpad' | 'discovery' | 'optimize'>('all');
  const [isOptimizeUpgradeOpen, setIsOptimizeUpgradeOpen] = useState(false);
  const [isCookieModalOpen, setIsCookieModalOpen] = useState(false);
  const { consent, showBanner, setConsent, updateConsent } = useCookieConsent();
  const [showCustomize, setShowCustomize] = useState(false);
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [openedFromSidebar, setOpenedFromSidebar] = useState(false);

  const { 
    user, 
    userProfile, 
    isLoggedIn, 
    isAuthReady, 
    userType, 
    setUserType,
    setUserProfile,
    setIsLoggedIn,
    handleLogin, 
    handleLogout,
    isSignUpModalOpen,
    setIsSignUpModalOpen,
    isSignInModalOpen,
    setIsSignInModalOpen,
    isRecoverKeyModalOpen,
    setIsRecoverKeyModalOpen,
    onSignUpPrompt,
    legalAcknowledged,
  } = useAuth();

  const {} = useReporting();

  const executePaidAction = useCallback((action: () => void) => {
    const tier = userProfile?.subscriptionTier || 'free';
    const isPremiumUser = tier === 'premium' || tier === 'premium_pro';
    if (isPremiumUser && !legalAcknowledged) {
      setOnLegalSuccess(() => action);
      setIsLegalModalOpen(true);
    } else {
      action();
    }
  }, [userProfile, legalAcknowledged]);

  const { 
    savedVentures: hookSavedVentures, 
    comparisonData, 
    loadingStates, 
    runComparison, 
    setComparisonData 
  } = useVentureIntelligence();

  const [savedVentures, setSavedVentures] = useState<BusinessIdea[]>([]);
  const [isSimplifiedLanguageMode, setIsSimplifiedLanguageMode] = useState(false);

  useEffect(() => {
    setSavedVentures(hookSavedVentures);
  }, [hookSavedVentures]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ideaId = params.get('idea');
    if (ideaId && savedVentures.length > 0 && !selectedIdea) {
      const idea = savedVentures.find(i => i.id === ideaId);
      if (idea) {
        setSelectedIdea(idea);
      }
    }
  }, [savedVentures, selectedIdea]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedIdea?.id) {
      params.set('idea', selectedIdea.id);
    } else {
      params.delete('idea');
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [selectedIdea]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const transactionId = params.get('transaction_id');
    const status = params.get('status');

    if (status === 'successful' && transactionId) {
      const verifyPayment = async () => {
        try {
          const response = await fetch(`/api/verify-payment?transaction_id=${transactionId}`);
          const data = await response.json();
          if (data.status === 'success') {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            window.location.reload();
          }
        } catch (err) {
          console.error('Payment verification failed:', err);
        }
      };
      verifyPayment();
    }
  }, []);

  const resultsRef = useRef<HTMLDivElement>(null);
  const launchpadLocRef = useRef<HTMLInputElement | null>(null);
  const discLocRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const fetchRates = async () => {
      const rates = await getExchangeRates();
      setExchangeRates(rates);
    };
    fetchRates();
  }, []);

  useEffect(() => {
    if (state.loading && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [state.loading]);

  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
  const [onLegalSuccess, setOnLegalSuccess] = useState<(() => void) | null>(null);

  useEffect(() => {
    const handleLegalModalTrigger = () => {
      setIsLegalModalOpen(true);
    };
    window.addEventListener('trigger-legal-modal', handleLegalModalTrigger);
    return () => window.removeEventListener('trigger-legal-modal', handleLegalModalTrigger);
  }, []);

  const handleOpenUpgradeModal = useCallback(() => {
    if (!user) {
      setIsSignInModalOpen(true);
      return;
    }
    setIsUpgradeModalOpen(true);
  }, [user, setIsSignInModalOpen]);

  useEffect(() => {
    const handleUpgradeTrigger = () => {
      handleOpenUpgradeModal();
    };
    const handleSavedTrigger = () => {
      setIsSavedMenuOpen(true);
    };
    window.addEventListener('trigger-upgrade', handleUpgradeTrigger);
    window.addEventListener('trigger-saved-opportunities', handleSavedTrigger);
    window.addEventListener('trigger-saved', handleSavedTrigger);
    return () => {
      window.removeEventListener('trigger-upgrade', handleUpgradeTrigger);
      window.removeEventListener('trigger-saved-opportunities', handleSavedTrigger);
      window.removeEventListener('trigger-saved', handleSavedTrigger);
    };
  }, [handleOpenUpgradeModal]);

  const handleToggleSave = useCallback(async (idea: BusinessIdea) => {
    if (!user) {
      setIsSignInModalOpen(true);
      return;
    }

    const isSaved = savedVentures.some(i => i.title === idea.title);

    if (isSaved) {
      const ideaToRemove = savedVentures.find(i => i.title === idea.title);
      if (ideaToRemove?.id) {
        try {
          await deleteDoc(doc(db, 'users', user.uid, 'savedIdeas', ideaToRemove.id));
        } catch (e) {
          handleFirestoreError(e, OperationType.DELETE, `users/${user.uid}/savedIdeas/${ideaToRemove.id}`);
        }
      }
    } else {
      const tier = userProfile?.subscriptionTier || 'free';
      if (tier === 'free') {
        handleOpenUpgradeModal();
        return;
      }
      try {
        const ideaRef = collection(db, 'users', user.uid, 'savedIdeas');
        await addDoc(ideaRef, {
          ...idea,
          date: new Date().toISOString()
        });
      } catch (e) {
        handleFirestoreError(e, OperationType.CREATE, `users/${user.uid}/savedIdeas`);
      }
    }
  }, [user, savedVentures, userProfile]);

  const handleClearIntelligence = useCallback(() => {
    setIsPurgeConfirmOpen(true);
  }, []);

  const confirmPurge = useCallback(async () => {
    if (!user) return;
    const userId = user.uid;

    try {
      for (const idea of savedVentures) {
        if (idea.id) {
          await deleteDoc(doc(db, 'users', userId, 'savedIdeas', idea.id));
        }
      }
      dispatch({ type: 'SET_STATE', payload: { recentScans: [] } });
      setIsSavedMenuOpen(false);
      setIsPurgeConfirmOpen(false);
    } catch (e) {
      handleFirestoreError(e, OperationType.DELETE, `users/${userId}/savedIdeas`);
    }
  }, [user, savedVentures, dispatch]);

  const handleSelectIdea = useCallback((idea: BusinessIdea) => {
    if (!isLoggedIn && state.searchCounts.view >= 1) {
      setIsSignUpModalOpen(true);
      return;
    }

    if (!isLoggedIn) {
      dispatch({ type: 'INCREMENT_SEARCH_COUNT', payload: 'view' });
    }

    setSelectedIdea(idea);
  }, [isLoggedIn, state.searchCounts.view, setIsSignUpModalOpen, dispatch]);

  const activeCurrency = useMemo(() => CURRENCIES.find(c => c.code === state.preferredCurrency) || CURRENCIES[0], [state.preferredCurrency]);

  const filteredCountries = useMemo(() => {
    return COUNTRIES.filter(c => c.toLowerCase().includes(state.countrySearch.toLowerCase()));
  }, [state.countrySearch]);

  const filteredCurrencies = useMemo(() => {
    const search = state.currencySearch.toLowerCase();
    return CURRENCIES.filter(c => 
      c.code.toLowerCase().includes(search) || 
      c.name.toLowerCase().includes(search) ||
      c.symbol.toLowerCase().includes(search)
    );
  }, [state.currencySearch]);

  const savedIdeasForComparison = useMemo(() => {
    return savedVentures.filter(i => i.hubOrigin === savedHubFilter);
  }, [savedVentures, savedHubFilter]);

  const [currentPathname, setCurrentPathname] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPathname(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleNavigateHome = useCallback(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    if (window.location.search || window.location.pathname !== '/') {
      window.location.href = '/';
    } else {
      window.location.reload();
    }
  }, []);

  const handleScrollToHowToUse = useCallback(() => {
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new Event('popstate'));
    }
    if (state.result || state.loading) {
      dispatch({ type: 'RESET_SEARCH' });
    }
    setTimeout(() => {
      const el = document.getElementById('how-to-use-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  }, [state.result, state.loading, dispatch]);

  const handleScrollToPricing = useCallback(() => {
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new Event('popstate'));
    }
    if (state.result || state.loading) {
      dispatch({ type: 'RESET_SEARCH' });
    }
    setSelectedIdea(null);
    setIsCompareViewOpen(false);
    setIsScrutinyActive(false);
    setIsSavedMenuOpen(false);
    setShowPrivacySettings(false);
    setIsCookieModalOpen(false);
    setIsSupportModalOpen(false);
    setIsSettingsModalOpen(false);

    const scrollToPricingTarget = () => {
      const el = document.getElementById('pricing-section') || document.getElementById('pricing-overview-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    };

    if (!scrollToPricingTarget()) {
      setTimeout(() => {
        if (!scrollToPricingTarget()) {
          setTimeout(scrollToPricingTarget, 150);
        }
      }, 100);
    }
  }, [state.result, state.loading, dispatch]);

  const handleOpenLegalContent = useCallback((path: string) => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isWebView = typeof window !== 'undefined' && (
      /wv|WebView/i.test(navigator.userAgent) ||
      window.location.protocol === 'file:' ||
      window.location.protocol === 'capacitor:' ||
      window.location.protocol === 'chrome-extension:' ||
      !(window.location.protocol.startsWith('http'))
    );

    const webBaseUrl = 'https://ais-pre-vopsowpbp3uzghxbuk2jnu-38169599415.europe-west2.run.app';

    if (isMobile || isWebView) {
      window.location.href = `${webBaseUrl}${path}`;
    } else {
      window.open(`${window.location.origin}${path}`, '_blank');
    }
  }, []);

  if (currentPathname.startsWith('/legal/') || currentPathname === '/settings/privacy') {
    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-[#322D29] text-white font-sans selection:bg-[#3a3430] selection:text-white pb-20 flex flex-col justify-between">
          <div>
            <Header 
              setIsUpgradeModalOpen={handleOpenUpgradeModal}
              setIsProfileMenuOpen={setIsProfileMenuOpen}
              onLogoClick={handleNavigateHome}
            />
            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-20">
              <div className="border border-white/[0.06] bg-[#3a3430] rounded-xl overflow-hidden shadow-2xl">
                {currentPathname === '/legal/terms' && (
                  <TermsOfServiceScreen />
                )}
                {currentPathname === '/legal/privacy' && (
                  <PrivacyPolicyScreen />
                )}
                {currentPathname === '/legal/cookie' && (
                  <CookiePolicyScreen />
                )}
                {currentPathname === '/legal/dpa' && (
                  <DataProcessingAgreementScreen />
                )}
                {currentPathname === '/legal/arbitration' && (
                  <ArbitrationAgreementScreen userId={user?.uid} />
                )}
                {currentPathname === '/settings/privacy' && (
                  <PrivacySettings user={user} userProfile={userProfile} savedVentures={savedVentures} />
                )}

                <div className="flex justify-between items-center px-6 md:px-10 pb-10 pt-6 bg-[#3a3430] border-t border-white/[0.06]">
                  <button
                    onClick={() => {
                      if (window.opener && !window.opener.closed) {
                        try {
                          window.opener.focus();
                        } catch (e) {
                          console.log('Failed to focus opener', e);
                        }
                        window.close();
                      } else {
                        window.history.pushState({}, '', '/');
                        window.dispatchEvent(new Event('popstate'));
                      }
                    }}
                    className="px-6 py-3 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[10px] font-bold uppercase tracking-widest rounded-lg transition-colors cursor-pointer font-heading"
                  >
                    <i className="fas fa-home mr-2"></i> Return to Home
                  </button>
                </div>
              </div>
            </main>
          </div>

          <footer className="w-full border-t border-white/[0.06] bg-[#322D29] py-12 px-6 sm:px-8 mt-auto shrink-0">
            <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <span className="text-[11px] font-bold tracking-[0.3em] text-white font-heading normal-case">eNvesta AI</span>
                <span className="text-[10px] text-white/60 block mt-1">© 2026 eNvesta AI from Bound Innovations. All rights reserved.</span>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-widest text-white/60 font-heading">
                <button 
                  onClick={handleScrollToPricing}
                  className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
                >
                  Pricing
                </button>
                <button 
                  onClick={handleScrollToHowToUse}
                  className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
                >
                  How to use
                </button>
                <button 
                  onClick={() => handleOpenLegalContent('/legal/terms')} 
                  className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
                >
                  Terms of Service
                </button>
                <button 
                  onClick={() => handleOpenLegalContent('/legal/privacy')} 
                  className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
                >
                  Privacy Policy
                </button>
                <button 
                  onClick={() => handleOpenLegalContent('/legal/cookie')}
                  className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
                >
                  Cookie Policy
                </button>
                <button 
                  onClick={() => setIsSignInModalOpen(true)}
                  className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
                >
                  Help and Feedback
                </button>
                <button 
                  onClick={() => {
                    setOpenedFromSidebar(false);
                    setShowPrivacySettings(true);
                  }}
                  className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
                >
                  Privacy Settings
                </button>
              </div>
            </div>
          </footer>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-[#322D29] text-[#FFFFFF] font-sans selection:bg-[#3a3430] selection:text-white pb-20">
        <Header 
          setIsUpgradeModalOpen={handleOpenUpgradeModal}
          setIsProfileMenuOpen={setIsProfileMenuOpen}
          onLogoClick={handleNavigateHome}
          onOpenSavedOpportunities={() => setIsSavedMenuOpen(true)}
          savedCount={savedVentures.length}
        />

        <Sidebar 
          isOpen={isProfileMenuOpen}
          onClose={() => setIsProfileMenuOpen(false)}
          user={user}
          userProfile={userProfile}
          userType={userType}
          isAuthReady={isAuthReady}
          setIsUpgradeModalOpen={handleOpenUpgradeModal}
          setIsSettingsModalOpen={setIsSettingsModalOpen}
          setIsSupportModalOpen={setIsSupportModalOpen}
          setIsSignOutModalOpen={setIsSignOutModalOpen}
          setSettingsInitialView={setSettingsInitialView}
          isTechnicalSuiteOpen={isTechnicalSuiteOpen}
          setIsTechnicalSuiteOpen={setIsTechnicalSuiteOpen}
          onPrivacySettingsClick={() => {
            setOpenedFromSidebar(true);
            setShowPrivacySettings(true);
          }}
          onOpenSavedOpportunities={() => {
            setIsProfileMenuOpen(false);
            setIsSavedMenuOpen(true);
          }}
          savedCount={savedVentures.length}
        />

        <Breadcrumbs 
          activeFeature={state.activeFeature}
          isRevealed={state.isRevealed}
          isScrutinyActive={isScrutinyActive}
          selectedIdea={selectedIdea}
          onNavigateHome={handleNavigateHome}
          onNavigateFeature={() => {
            setSelectedIdea(null);
          }}
        />

      <main className="max-w-[1600px] mx-auto px-4 sm:px-8 py-8 md:py-16 space-y-16 md:space-y-32">
        {!state.isRevealed && !state.loading && (
          <div className="space-y-16 md:space-y-24">
            <InteractiveHeroPreview
              activeCurrency={activeCurrency}
              filteredCurrencies={filteredCurrencies}
              exchangeRates={exchangeRates}
              currentMinBudget={currentMinBudget}
              currentMaxBudget={currentMaxBudget}
              launchpadLocRef={launchpadLocRef}
              discLocRef={discLocRef}
              filteredCountries={filteredCountries}
              user={user}
              userProfile={userProfile}
              isLoggedIn={isLoggedIn}
              onOpenUpgradeModal={handleOpenUpgradeModal}
              onOpenSignUpModal={(source?: LandingLoginSource) => {
                if (source) {
                  dispatch({ type: 'SET_LANDING_LOGIN_SOURCE', payload: source });
                }
                setIsSignUpModalOpen(true);
              }}
              executePaidAction={executePaidAction}
              setIsScrutinyActive={setIsScrutinyActive}
              onOpenSavedOpportunities={() => setIsSavedMenuOpen(true)}
              savedCount={savedVentures.length}
            />

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            >
              <RecentScans 
                scans={state.recentScans} 
                onRecentClick={handleRecentClick} 
                subscriptionTier={userProfile?.subscriptionTier || 'free'}
                onUpgradePrompt={handleOpenUpgradeModal}
                onOpenSavedOpportunities={() => setIsSavedMenuOpen(true)}
                savedCount={savedVentures.length}
              />
            </motion.div>

            <div id="pricing-section" className="scroll-mt-20">
              <PricingOverview 
                onOpenUpgradeModal={handleOpenUpgradeModal}
                currentTier={userProfile?.subscriptionTier || 'free'}
                preferredCurrency={state.preferredCurrency}
              />
            </div>

            <div id="how-to-use-section" className="scroll-mt-20">
              <HowToUse isVisible={true} />
            </div>
          </div>
        )}

        {(state.loading || state.result) && (
          <div ref={resultsRef} className="space-y-8 scroll-mt-24">
            {state.isRevealed && !state.loading && (
              <div className="flex items-center justify-start gap-4 mb-4">
                <button onClick={() => dispatch({ type: 'RESET_SEARCH' })} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/60 hover:text-white transition-colors cursor-pointer font-heading"><i className="fas fa-arrow-left"></i> New Recon</button>
                <button onClick={() => setIsSavedMenuOpen(true)} className="relative w-11 h-11 border border-white/10 bg-[#3a3430] rounded-lg flex items-center justify-center text-white hover:bg-white/10 transition-all cursor-pointer"><i className="fas fa-bookmark text-sm"></i>{savedVentures.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F26522] text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-[#322D29]">{savedVentures.length}</span>}</button>
              </div>
            )}
            <AnalysisResultView loading={state.loading} data={state.result} featureType={state.activeFeature || FeatureType.Scan} onSelectIdea={handleSelectIdea} />
          </div>
        )}
      </main>

      <footer className="w-full border-t border-white/[0.06] bg-[#322D29] py-16 px-6 sm:px-8 mt-20 shrink-0 relative">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center justify-between gap-8 animate-in fade-in duration-500 text-left">
          <div>
            <span className="text-[11px] font-bold tracking-[0.3em] text-white font-heading normal-case">eNvesta AI</span>
            <span className="text-[10px] text-white/60 block mt-1.5 leading-relaxed">© 2026 eNvesta AI from Bound Innovations. All rights reserved.</span>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-[10px] uppercase tracking-widest text-white/60 font-heading">
            <button 
              onClick={handleScrollToPricing}
              className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
            >
              Pricing
            </button>
            <button 
              onClick={handleScrollToHowToUse}
              className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
            >
              How to use
            </button>
            <button 
              onClick={() => handleOpenLegalContent('/legal/terms')} 
              className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
            >
              Terms of Service
            </button>
            <button 
              onClick={() => handleOpenLegalContent('/legal/privacy')} 
              className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => handleOpenLegalContent('/legal/cookie')}
              className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
            >
              Cookie Policy
            </button>
            <button 
              onClick={() => setIsSignInModalOpen(true)} 
              className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
            >
              Help and Feedback
            </button>
            <button 
              onClick={() => {
                setOpenedFromSidebar(false);
                setShowPrivacySettings(true);
              }}
              className="hover:text-[#F26522] transition-colors cursor-pointer text-white/60"
            >
              Privacy Settings
            </button>
          </div>
        </div>
      </footer>

      {state.activeFeature === FeatureType.Optimize && (
        <OptimizeDashboard 
          onClose={() => dispatch({ type: 'SET_ACTIVE_FEATURE', payload: null })}
          savedVentures={savedVentures}
        />
      )}

      {selectedIdea && (selectedIdea.hubOrigin === 'discovery' ? (
        <DiscoveryDossier 
          idea={selectedIdea} 
          onClose={() => setSelectedIdea(null)} 
          isSaved={savedVentures.some(i => i.title === selectedIdea.title)} 
          onToggleSave={() => handleToggleSave(selectedIdea)} 
          onUpgradePrompt={handleOpenUpgradeModal}
        />
      ) : (
        <BusinessDetail 
          idea={selectedIdea} 
          onClose={() => setSelectedIdea(null)} 
          isSaved={savedVentures.some(i => i.title === selectedIdea.title)} 
          onToggleSave={() => handleToggleSave(selectedIdea)} 
          onUpgradePrompt={handleOpenUpgradeModal}
        />
      ))}
      {state.isMapPickerOpen && <MapPicker onSelect={handleMapSelection} onClose={() => dispatch({ type: 'SET_MAP_PICKER', payload: { isOpen: false } })} />}

      {isSavedMenuOpen && isAuthReady && (
        <SavedIntelDrawer 
          isOpen={isSavedMenuOpen}
          onClose={() => setIsSavedMenuOpen(false)}
          savedVentures={savedVentures}
          isPurgeConfirmOpen={isPurgeConfirmOpen}
          setIsPurgeConfirmOpen={setIsPurgeConfirmOpen}
          handleClearIntelligence={handleClearIntelligence}
          confirmPurge={confirmPurge}
          savedHubFilter={savedHubFilter}
          setSavedHubFilter={setSavedHubFilter}
          setSelectedIdea={setSelectedIdea}
          handleToggleSave={handleToggleSave}
          setIsCompareViewOpen={(open) => {
            if (open) {
              executePaidAction(() => setIsCompareViewOpen(true));
            } else {
              setIsCompareViewOpen(false);
            }
          }}
          setIsOptimizeUpgradeOpen={setIsOptimizeUpgradeOpen}
          subscriptionTier={userProfile?.subscriptionTier || 'free'}
          onUpgradePrompt={handleOpenUpgradeModal}
        />
      )}
      {isOptimizeUpgradeOpen && (
        <OptimizeUpgradeFlow 
          isOpen={isOptimizeUpgradeOpen} 
          onClose={() => setIsOptimizeUpgradeOpen(false)} 
        />
      )}
      {isCompareViewOpen && (
        <CompareView 
          savedIdeas={savedIdeasForComparison} 
          onClose={() => setIsCompareViewOpen(false)} 
          preferredCurrency={state.preferredCurrency}
          onSelectIdea={handleSelectIdea}
          comparisonData={comparisonData}
          loadingStates={loadingStates}
          onRunComparison={runComparison}
          onSetComparisonData={setComparisonData}
        />
      )}
      {isScrutinyActive && (
        <ScrutinyDeck 
          onClose={() => setIsScrutinyActive(false)}
          onSelectIdea={handleSelectIdea}
        />
      )}
      <ModalPortal>
        <UpgradeModal 
          isOpen={isUpgradeModalOpen} 
          onClose={() => setIsUpgradeModalOpen(false)} 
          userType={userType}
          userEmail={user?.email || undefined}
          userId={user?.uid}
        />

        <LegalAcknowledgmentModal
          isOpen={isLegalModalOpen}
          onClose={() => setIsLegalModalOpen(false)}
          onSuccess={() => {
            if (onLegalSuccess) {
              onLegalSuccess();
              setOnLegalSuccess(null);
            }
          }}
        />

        <RecoverKeyModal
          isOpen={isRecoverKeyModalOpen}
          onClose={() => setIsRecoverKeyModalOpen(false)}
          onBackToSignIn={() => {
            setIsRecoverKeyModalOpen(false);
            setIsSignInModalOpen(true);
          }}
        />

        <SignOutModal
          isOpen={isSignOutModalOpen}
          onClose={() => setIsSignOutModalOpen(false)}
          onConfirm={handleLogout}
        />

        <SupportModal
          isOpen={isSupportModalOpen}
          onClose={() => setIsSupportModalOpen(false)}
          userEmail={user?.email || undefined}
        />

        <SettingsModal
          isOpen={isSettingsModalOpen}
          onClose={() => {
            setIsSettingsModalOpen(false);
            setSettingsInitialView(null);
          }}
          initialView={settingsInitialView}
        />

        <CookiePolicyModal
          isOpen={isCookieModalOpen}
          onClose={() => setIsCookieModalOpen(false)}
        />
      </ModalPortal>
      <EnhancedAppFeatures 
        user={user}
        userProfile={userProfile}
        isLoggedIn={isLoggedIn}
        onSignUpPrompt={onSignUpPrompt}
        savedVentures={savedVentures}
        setSavedVentures={setSavedVentures}
        onVentureClick={handleSelectIdea}
        setAppState={(payload: any) => {
          if (typeof payload === 'function') {
            const current = { 
              activeFeature: state.activeFeature, 
              isRevealed: state.isRevealed, 
              loading: state.loading, 
              result: state.result 
            };
            const next = payload(current);
            dispatch({ type: 'SET_STATE', payload: next });
          } else {
            dispatch({ type: 'SET_STATE', payload });
          }
        }}
        onSelectLanguageSimplified={setIsSimplifiedLanguageMode}
        isSimplifiedLanguageMode={isSimplifiedLanguageMode}
        isTechnicalSuiteOpen={isTechnicalSuiteOpen}
        setIsTechnicalSuiteOpen={setIsTechnicalSuiteOpen}
      />

      <AnimatePresence>
        {showBanner && (
          <CookieBanner 
            key="app-cookie-banner"
            onAcceptAll={() => setConsent({ ...defaultConsent, region: 'EU', timestamp: Date.now() })}
            onEssentialOnly={() => setConsent({ ...euDefaultConsent, timestamp: Date.now() })}
            onCustomize={() => setShowCustomize(true)}
          />
        )}

        {showCustomize && consent && (
          <CookieCustomizeModal
            key="app-cookie-customize-modal"
            consent={consent}
            onSave={updateConsent}
            onClose={() => setShowCustomize(false)}
          />
        )}

        {showPrivacySettings && (
          <PrivacySettings
            key="app-privacy-settings-modal"
            isModal={true}
            onClose={() => setShowPrivacySettings(false)}
            user={user}
            userProfile={userProfile}
            savedVentures={savedVentures}
            openedFromSidebar={openedFromSidebar}
          />
        )}
      </AnimatePresence>
      </div>
    </ErrorBoundary>
  );
};

export default App;
